/**
 * OrgChart Business Logic
 * Handles normalization, tree building, and promotion rules
 * Updated to work with both Firestore data and new API data structure
 */

/**
 * Normalize a name: lowercase, trim, collapse spaces
 */
export function normalizeName(str) {
  if (!str || typeof str !== 'string') return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Check if data comes from API (has agentLevel field)
 */
export function isApiData(user) {
  return user && typeof user.agentLevel === 'string';
}

/**
 * Get recruiter field from user (handles both API and Firestore data)
 */
export function getRecruiterField(user) {
  if (!user) return null;

  // For API data, use recruiterName
  if (isApiData(user)) {
    return user.recruiterName || user.recruiter;
  }

  // For Firestore data, check multiple potential fields
  return user.recruiter || user.recruiterName || user.upline || user.leader || user.parent || user.supervisor;
}

/**
 * Get account type from user (handles both API and Firestore data)
 */
export function getAccountType(user) {
  if (!user) return 'Agent';

  // For API data, use the mapped accountType
  if (isApiData(user)) {
    return user.plan || 'Agent';
  }

  // For Firestore data, use accountType directly
  return user.plan || 'Agent';
}

/**
 * Split multi-recruiter strings (e.g., "John & Jane" or "John/Jane")
 * Returns the first token as the primary recruiter
 */
export function splitMultiRecruiter(str) {
  if (!str || typeof str !== 'string') return '';

  // Split on common delimiters: &, /, comma
  const tokens = str.split(/[&/,]/).map(t => t.trim()).filter(Boolean);
  return tokens.length > 0 ? tokens[0] : '';
}

/**
 * Build indexes from user array
 * @param {Array} users - Array of user documents from Firestore
 * @returns {{ byName: Object, childrenMap: Object }}
 */
export function buildIndexes(users) {
  const byName = {};
  const childrenMap = {};

  console.log('Building indexes for', users.length, 'users');

  // Build byName index - create multiple keys for each user
  users.forEach(user => {
    if (!user.name) return;

    // Create multiple normalized versions to handle different name formats
    const nameVariations = [
      user.name, // Original name
      splitMultiRecruiter(user.name), // First part only
      user.name.replace(/&/g, 'and').replace(/\s+/g, ' '), // Replace & with and
      user.name.replace(/\s*&\s*/g, ' '), // Remove & completely
    ];

    // Add all variations to byName index
    nameVariations.forEach(name => {
      const normalized = normalizeName(name);
      if (normalized && !byName[normalized]) {
        byName[normalized] = user;
        console.log(`Added to byName: "${normalized}" -> ${user.name}`);
      }
    });
  });

  console.log('byName index has', Object.keys(byName).length, 'entries');

  // Build childrenMap (parent -> children)
  users.forEach(user => {
    // Use the new utility function to get recruiter field
    const recruiterField = getRecruiterField(user);
    if (!recruiterField) {
      console.log(`User ${user.name} has no recruiter field`);
      return;
    }

    // Create multiple variations of the recruiter name
    const recruiterVariations = [
      recruiterField,
      splitMultiRecruiter(recruiterField),
      recruiterField.replace(/&/g, 'and').replace(/\s+/g, ' '),
      recruiterField.replace(/\s*&\s*/g, ' '),
    ];

    let matched = false;
    for (const recruiterName of recruiterVariations) {
      const normalizedRecruiter = normalizeName(recruiterName);

      if (normalizedRecruiter && byName[normalizedRecruiter]) {
        if (!childrenMap[normalizedRecruiter]) {
          childrenMap[normalizedRecruiter] = [];
        }
        childrenMap[normalizedRecruiter].push(user);
        console.log(`✓ Mapped ${user.name} -> ${normalizedRecruiter} (from "${recruiterField}")`);
        matched = true;
        break;
      }
    }

    if (!matched) {
      console.warn(`✗ Could not map ${user.name} -> ${recruiterField} (tried ${recruiterVariations.length} variations)`);
    }
  });

  // Log the childrenMap for debugging
  console.log('Children Map:', Object.keys(childrenMap).length, 'leaders with children');
  Object.keys(childrenMap).forEach(leader => {
    console.log(`  ${leader} has ${childrenMap[leader].length} children`);
  });

  return { byName, childrenMap };
}

/**
 * Find the root Broker for a given user
 * If user is a Broker, they are the root
 * Otherwise, walk up the recruiter chain until finding a Broker
 */
export function findRootBroker(currentUser, byName) {
  if (!currentUser) return null;

  // If current user is a Broker, they are the root
  if (getAccountType(currentUser) === 'Broker') {
    return currentUser;
  }

  // Walk up the chain
  const visited = new Set(); // Prevent infinite loops
  let current = currentUser;

  while (current) {
    const recruiterField = getRecruiterField(current);
    if (!recruiterField) break;

    const normalized = normalizeName(current.name);
    if (visited.has(normalized)) {
      console.warn('Cycle detected in recruiter chain for:', current.name);
      break;
    }
    visited.add(normalized);

    const primaryRecruiter = splitMultiRecruiter(recruiterField);
    const normalizedRecruiter = normalizeName(primaryRecruiter);
    const parent = byName[normalizedRecruiter];

    if (!parent) break;

    if (getAccountType(parent) === 'Broker') {
      return parent;
    }

    current = parent;
  }

  return null; // No broker found in chain
}

/**
 * Build BaseShop tree - excludes promoted Brokers and their subtrees
 * Stops recursion when encountering a Broker (doesn't traverse into their org)
 */
export function buildBaseShopTree(root, { byName, childrenMap }) {
  const visited = new Set();

  function buildNode(user) {
    // Try multiple name variations to find children
    const nameVariations = [
      user.name,
      splitMultiRecruiter(user.name),
      user.name.replace(/&/g, 'and').replace(/\s+/g, ' '),
      user.name.replace(/\s*&\s*/g, ' '),
    ];

    let normalized = '';
    let children = [];

    // Find which variation has children
    for (const name of nameVariations) {
      const testNormalized = normalizeName(name);
      if (testNormalized && childrenMap[testNormalized]) {
        normalized = testNormalized;
        children = childrenMap[testNormalized];
        console.log(`Found ${children.length} children for ${user.name} using variation: "${testNormalized}"`);
        break;
      }
    }

    // Prevent infinite loops
    if (visited.has(normalized || user.name)) {
      console.warn('Cycle detected at:', user.name);
      return null;
    }
    visited.add(normalized || user.name);

    const node = {
      id: user.id || normalized || user.name,
      name: user.name,
      plan: getAccountType(user),
      recruiter: getRecruiterField(user),
      email: user.email,
      profilePicture: user.profilePicture || user.photoURL || '',
      photoURL: user.photoURL || user.profilePicture || '',
      level: user.level || getAccountType(user) || '',
      children: []
    };

    console.log(`Building node for ${user.name} with ${children.length} children`);

    // For each child, if they're NOT a Broker, recurse
    // If they ARE a Broker, exclude them entirely (don't add to tree)
    children.forEach(child => {
      if (getAccountType(child) !== 'Broker') {
        const childNode = buildNode(child);
        if (childNode) {
          node.children.push(childNode);
        }
      }
      // If child is a Broker, skip them and their subtree
    });

    console.log(`Final node for ${user.name} has ${node.children.length} children`);
    return node;
  }

  return buildNode(root);
}

/**
 * Collect immediate Brokers that originated from root's organization
 * Returns array of Broker users whose first Broker ancestor is the root
 */
export function collectImmediateBrokers(root, { byName, childrenMap }) {
  const brokers = [];
  const visited = new Set();
  const rootNormalized = normalizeName(root.name);

  function traverse(user, ancestorBroker) {
    const normalized = normalizeName(user.name);

    if (visited.has(normalized)) return;
    visited.add(normalized);

    // If this user is a Broker and their ancestor is root, collect them
    if (getAccountType(user) === 'Broker' && ancestorBroker === rootNormalized) {
      brokers.push(user);
      // Continue traversing their downline with THEM as the new ancestor
      ancestorBroker = normalized;
    }

    // Traverse children
    const children = childrenMap[normalized] || [];
    children.forEach(child => {
      traverse(child, ancestorBroker);
    });
  }

  // Start traversal from root's children
  const children = childrenMap[rootNormalized] || [];
  children.forEach(child => {
    traverse(child, rootNormalized);
  });

  // Deduplicate by id
  const uniqueBrokers = [];
  const seenIds = new Set();
  brokers.forEach(broker => {
    const id = broker.id || normalizeName(broker.name);
    if (!seenIds.has(id)) {
      seenIds.add(id);
      uniqueBrokers.push(broker);
    }
  });

  return uniqueBrokers;
}

/**
 * Build SuperTeam tree - includes ALL users including promoted Brokers
 * Brokers are rendered as sub-roots with their entire subtrees attached
 */
export function buildSuperTeamTree(root, { byName, childrenMap }) {
  const visited = new Set();

  function buildNode(user) {
    // Try multiple name variations to find children
    const nameVariations = [
      user.name,
      splitMultiRecruiter(user.name),
      user.name.replace(/&/g, 'and').replace(/\s+/g, ' '),
      user.name.replace(/\s*&\s*/g, ' '),
    ];

    let normalized = '';
    let children = [];

    // Find which variation has children
    for (const name of nameVariations) {
      const testNormalized = normalizeName(name);
      if (testNormalized && childrenMap[testNormalized]) {
        normalized = testNormalized;
        children = childrenMap[testNormalized];
        console.log(`Found ${children.length} children for ${user.name} using variation: "${testNormalized}"`);
        break;
      }
    }

    // Prevent infinite loops
    if (visited.has(normalized || user.name)) {
      console.warn('Cycle detected at:', user.name);
      return null;
    }
    visited.add(normalized || user.name);

    const node = {
      id: user.id || normalized || user.name,
      name: user.name,
      plan: getAccountType(user),
      recruiter: getRecruiterField(user),
      email: user.email,
      profilePicture: user.profilePicture || user.photoURL || '',
      photoURL: user.photoURL || user.profilePicture || '',
      level: user.level || getAccountType(user) || '',
      children: [],
      isBrokerSubRoot: getAccountType(user) === 'Broker' && user.id !== root.id
    };

    console.log(`Building SuperTeam node for ${user.name} with ${children.length} children`);

    // Get all children and recurse
    children.forEach(child => {
      const childNode = buildNode(child);
      if (childNode) {
        node.children.push(childNode);
      }
    });

    console.log(`Final SuperTeam node for ${user.name} has ${node.children.length} children`);
    return node;
  }

  return buildNode(root);
}

/**
 * Find a node in the tree by name (for search/focus)
 */
export function findNodeByName(tree, searchName) {
  const normalizedSearch = normalizeName(searchName);

  function search(node) {
    if (normalizeName(node.name) === normalizedSearch) {
      return node;
    }

    if (node.children) {
      for (const child of node.children) {
        const found = search(child);
        if (found) return found;
      }
    }

    return null;
  }

  return search(tree);
}

/**
 * Get path from root to a specific node (for focus/expand)
 */
export function getPathToNode(tree, targetId) {
  function search(node, currentPath) {
    const newPath = [...currentPath, node.id];

    if (node.id === targetId) {
      return newPath;
    }

    if (node.children) {
      for (const child of node.children) {
        const found = search(child, newPath);
        if (found) return found;
      }
    }

    return null;
  }

  return search(tree, []);
}

/**
 * Build tracker index from associate and prospect tracker data
 * @param {Array} associateDocs - Associate tracker documents
 * @param {Array} prospectDocs - Prospect tracker documents
 * @param {Object} byName - User index by normalized name
 * @returns {Object} Tracker index with Sets for each flag
 */
export function buildTrackerIndex(associateDocs, prospectDocs, byName) {
  const isTraining = new Set();
  const isBigEvent = new Set();
  const isKeyPlayer = new Set();
  const isLicensed = new Set();
  const isClient = new Set();

  // Process associate tracker
  associateDocs.forEach(doc => {
    let userId = doc.userId || doc.id;

    // Fallback: try to find user by email or name
    if (!userId && (doc.email || doc.name)) {
      const key = normalizeName(doc.email || doc.name);
      const user = byName[key];
      if (user) userId = user.id;
    }

    if (!userId) return;

    // BPM Attendance (training)
    if (doc.training === true) {
      isTraining.add(userId);
    }

    // Big Event (check multiple fields that might indicate big event ticket/attendance)
    if (doc.bigEvent === true || doc.hasBigEventTicket === true || doc.bigEventTicket === true || doc.ticketPurchased === true) {
      isBigEvent.add(userId);
    }

    // Key Player
    if (doc.keyPlayer === true) {
      isKeyPlayer.add(userId);
    }

    // Licensed (getLicensed also sets isLicensed)
    if (doc.getLicensed === true || doc.isLicensed === true) {
      isLicensed.add(userId);
    }
  });

  // Process prospect tracker for Client status
  prospectDocs.forEach(doc => {
    let userId = doc.userId || doc.ownerId || doc.agentId;

    // Fallback: try to find user by email or name
    if (!userId && (doc.email || doc.name || doc.agentEmail)) {
      const key = normalizeName(doc.email || doc.name || doc.agentEmail);
      const user = byName[key];
      if (user) userId = user.id;
    }

    if (!userId) return;

    // Check if any prospect has "Client" status
    if (doc.status === 'Client' || doc.status === 'client') {
      isClient.add(userId);
    }
  });

  return {
    isTraining,
    isBigEvent,
    isKeyPlayer,
    isLicensed,
    isClient,
  };
}

/**
 * Collect Net License for a leader (unlicensed subtrees under that leader)
 * @param {string} leaderId - The leader's user ID
 * @param {Object} childrenMap - Map of normalized name to children array
 * @param {Object} byId - Map of user ID to user object
 * @param {Function} isLicensedFn - Function to check if user is licensed
 * @returns {Set} Set of user IDs that are "net licensed" under this leader
 */
export function collectNetLicenseForLeader(leaderId, childrenMap, byId, isLicensedFn) {
  const net = new Set();

  // Helper to include entire subtree
  const includeSubtree = (userId) => {
    const user = byId[userId];
    if (!user) return;

    const stack = [userId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      const currentUser = byId[currentId];
      if (!currentUser) continue;

      net.add(currentId);

      const currentNormalized = normalizeName(currentUser.name);
      const children = childrenMap[currentNormalized] || [];

      children.forEach(child => {
        stack.push(child.id);
      });
    }
  };

  // Recursive traverse
  const traverse = (userId) => {
    const user = byId[userId];
    if (!user) return;

    // If unlicensed, take entire subtree
    if (!isLicensedFn(userId)) {
      includeSubtree(userId);
      return;
    }

    // If licensed, search for unlicensed pockets below
    const normalized = normalizeName(user.name);
    const children = childrenMap[normalized] || [];

    children.forEach(child => {
      traverse(child.id);
    });
  };

  // Start from leader's direct children
  const leader = byId[leaderId];
  if (leader) {
    const leaderNormalized = normalizeName(leader.name);
    const children = childrenMap[leaderNormalized] || [];

    children.forEach(child => {
      traverse(child.id);
    });
  }

  return net;
}

